package org.teddapp.project.ui.finance

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Expense_entry
import com.tedd.db.Expense_list_item
import com.tedd.db.Finance_account
import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.ui.LocalTeddFonts
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCheckboxRow
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddSize
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/**
 * Le portefeuille d'une ligne de liste : son montant prévu est une enveloppe, on y note
 * ce qu'on en dépense, petit à petit. Le réel de la ligne est la somme de ces sous-dépenses,
 * et c'est elle que la liste mère compte.
 *
 * Chaque sous-dépense peut se payer tout de suite depuis un compte : elle porte alors sa
 * transaction, marquée « payé » dans la liste. Cocher la ligne ne débite ensuite que le
 * restant. Sans ce choix, la sous-dépense n'est qu'une note, et c'est cocher la ligne qui paie.
 */
@Composable
internal fun ExpenseWalletPanel(
    item: Expense_list_item,
    entries: List<Expense_entry>,
    currency: String,
    accounts: List<Finance_account>,
    preferredAccountId: String?,
    onAdd: (label: String, amount: Long, date: String?) -> Unit,
    onAddPaid: (label: String, amount: Long, date: String?, accountId: String) -> Unit,
    onUpdate: (entry: Expense_entry, label: String, amount: Long, date: String?) -> Unit,
    onDelete: (Expense_entry) -> Unit,
    onClose: () -> Unit,
) {
    val spent = entries.sumOf { it.amount }
    val left = item.planned - spent
    var editing by remember(item.id) { mutableStateOf<Expense_entry?>(null) }
    var label by remember(item.id) { mutableStateOf("") }
    var amount by remember(item.id) { mutableStateOf("") }
    var date by remember(item.id) { mutableStateOf(TODAY_ISO) }
    // Payer maintenant : coché par choix, jamais par défaut ; le compte par défaut est préréglé.
    var payNow by remember(item.id) { mutableStateOf(false) }
    var accountId by remember(item.id) { mutableStateOf(preferredAccountId ?: accounts.firstOrNull { it.is_deleted == 0L }?.id) }
    val amountValue = amount.toLongOrNull() ?: 0L
    val canSave = label.isNotBlank() && amountValue > 0L && (!payNow || editing != null || accountId != null)
    fun clear() { editing = null; label = ""; amount = ""; date = TODAY_ISO }
    fun save() {
        if (!canSave) return
        val d = date.trim().takeIf { org.teddapp.project.ui.TeddDates.isValidIso(it) }
        val e = editing
        val acc = accountId
        when {
            e != null -> onUpdate(e, label, amountValue, d)
            payNow && acc != null -> onAddPaid(label, amountValue, d, acc)
            else -> onAdd(label, amountValue, d)
        }
        clear()
    }
    val fullScreen = org.teddapp.project.ui.LocalUseFullScreenForm.current

    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false, dismissOnClickOutside = true)) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .then(if (fullScreen) Modifier.background(TeddTokens.Bg) else Modifier.background(Color.Black.copy(alpha = 0.35f)))
                .clickable(remember { MutableInteractionSource() }, indication = null, onClick = onClose),
        ) {
            Column(
                modifier = Modifier
                    .then(if (fullScreen) Modifier.fillMaxSize() else Modifier.align(Alignment.Center).width(TeddSize.SidePanel).fillMaxHeight(0.86f))
                    .background(TeddTokens.Bg)
                    .then(if (fullScreen) Modifier.imePadding() else Modifier)
                    .clickable(remember { MutableInteractionSource() }, indication = null, onClick = {}),
            ) {
                // En-tête
                Row(
                    modifier = Modifier.fillMaxWidth().padding(start = 16.dp, end = 12.dp, top = if (fullScreen) 56.dp else 18.dp, bottom = 14.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(Modifier.weight(1f)) {
                        Text("PORTEFEUILLE", color = TeddTokens.InkMuted, fontSize = 10.sp, fontWeight = FontWeight.Bold, letterSpacing = 0.6.sp)
                        Spacer(Modifier.height(2.dp))
                        Text(item.name, color = TeddTokens.Ink, fontSize = 17.sp, fontWeight = FontWeight.SemiBold, maxLines = 2)
                    }
                    Box(
                        modifier = Modifier
                            .size(36.dp)
                            .clip(CircleShape)
                            .background(TeddTokens.Surface, CircleShape)
                            .border(1.dp, TeddTokens.Line, CircleShape)
                            .clickable(remember { MutableInteractionSource() }, indication = null, onClick = onClose),
                        contentAlignment = Alignment.Center,
                    ) { TeddIcon(name = "x", size = 14.dp, color = TeddTokens.InkMuted, strokeWidth = 1.8.dp) }
                }
                Column(
                    modifier = Modifier.weight(1f).fillMaxWidth().verticalScroll(rememberScrollState()).padding(horizontal = 16.dp),
                    verticalArrangement = Arrangement.spacedBy(14.dp),
                ) {
                    WalletSummary(item.planned, spent, left, currency)
                    // Ajouter ou modifier une sous-dépense
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                            .padding(14.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp),
                    ) {
                        Text(
                            if (editing == null) "Nouvelle sous-dépense" else "Modifier la sous-dépense",
                            color = TeddTokens.Ink, style = TeddType.BodyStrong,
                        )
                        WalletField(label, "Ex. Marché, pain, huile…", onChange = { label = it })
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Box(Modifier.weight(1f)) {
                                WalletField(amount, "Montant", digits = true, mono = true, onChange = { v -> if (v.all { it.isDigit() }) amount = v })
                            }
                            Box(Modifier.width(130.dp)) {
                                WalletField(date, "AAAA-MM-JJ", mono = true, onChange = { date = it })
                            }
                        }
                        if (amountValue > 0 && amountValue > left + (editing?.amount ?: 0L)) {
                            Text("Ça dépasse ce qui reste dans l'enveloppe.", color = TeddTokens.Danger, style = TeddType.Caption)
                        }
                        // Seulement à l'ajout : une sous-dépense déjà écrite garde son mode de paiement.
                        if (editing == null && accounts.any { it.is_deleted == 0L }) {
                            TeddCheckboxRow("Payer maintenant depuis un compte", checked = payNow, onCheckedChange = { payNow = it })
                            if (payNow) FinanceAccountChips(accounts, selectedId = accountId, onSelect = { accountId = it })
                        } else if (editing?.tx_id != null) {
                            Text("Payée depuis un compte : sa transaction suit le montant et la date.", color = TeddTokens.InkMuted, style = TeddType.Caption)
                        }
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            TeddButton(
                                text = if (editing == null) "Ajouter" else "Enregistrer",
                                onClick = ::save,
                                kind = TeddButtonKind.Primary,
                                size = TeddButtonSize.Sm,
                                enabled = canSave,
                            )
                            if (editing != null) TeddButton(text = "Annuler", onClick = ::clear, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
                        }
                    }
                    // Les sous-dépenses, de la plus récente à la plus ancienne
                    if (entries.isEmpty()) {
                        Text(
                            "Aucune sous-dépense pour l'instant. Note ici ce que tu prends sur cette ligne : le réel se calcule tout seul.",
                            color = TeddTokens.InkMuted, style = TeddType.Caption,
                        )
                    } else {
                        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                            entries.sortedWith(compareByDescending<Expense_entry> { it.date.orEmpty() }.thenByDescending { it.created_at }).forEach { e ->
                                EntryRow(
                                    entry = e,
                                    currency = currency,
                                    selected = editing?.id == e.id,
                                    onEdit = {
                                        editing = e; label = e.label; amount = e.amount.toString(); date = e.date ?: TODAY_ISO
                                    },
                                    onDelete = { onDelete(e); if (editing?.id == e.id) clear() },
                                )
                            }
                        }
                    }
                    val paid = entries.filter { it.tx_id != null }.sumOf { it.amount }
                    Text(
                        if (paid > 0L) "Déjà payé depuis un compte : ${money(paid, currency)}. Coche la ligne dans la liste pour payer le restant."
                        else "Coche la ligne dans la liste pour la payer depuis un compte : le montant proposé sera ce total.",
                        color = TeddTokens.InkSubtle, style = TeddType.Caption,
                        modifier = Modifier.padding(bottom = 16.dp),
                    )
                }
            }
        }
    }
}

/** Prévu, dépensé, reste — et la barre qui les montre d'un coup d'œil. */
@Composable
private fun WalletSummary(planned: Long, spent: Long, left: Long, currency: String) {
    val over = left < 0
    val ratio = if (planned <= 0) 1f else (spent.toFloat() / planned).coerceIn(0f, 1f)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Row(Modifier.fillMaxWidth()) {
            SummaryCell("Prévu", money(planned, currency), TeddTokens.Ink, Modifier.weight(1f))
            SummaryCell("Dépensé", money(spent, currency), TeddTokens.Ink, Modifier.weight(1f))
            SummaryCell(if (over) "Dépassé de" else "Reste", money(if (over) -left else left, currency), if (over) TeddTokens.Danger else TeddTokens.Success, Modifier.weight(1f))
        }
        Box(Modifier.fillMaxWidth().height(6.dp).clip(RoundedCornerShape(3.dp)).background(TeddTokens.SurfaceSunken)) {
            Box(Modifier.fillMaxWidth(ratio).height(6.dp).background(if (over) TeddTokens.Danger else TeddTokens.LimeStrong))
        }
    }
}

@Composable
private fun SummaryCell(label: String, value: String, color: Color, modifier: Modifier) {
    Column(modifier) {
        Text(label, color = TeddTokens.InkMuted, fontSize = 11.sp)
        Text(value, color = color, style = TeddType.Amount, maxLines = 1)
    }
}

@Composable
private fun EntryRow(entry: Expense_entry, currency: String, selected: Boolean, onEdit: () -> Unit, onDelete: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(if (selected) TeddTokens.LimeSoft else TeddTokens.Surface)
            .clickable(onClick = onEdit)
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Column(Modifier.weight(1f)) {
            Text(entry.label, color = TeddTokens.Ink, fontSize = 13.sp, maxLines = 1)
            entry.date?.let { Text(dateLabel(it), color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp)) }
        }
        // Payée tout de suite : la transaction existe, cocher la ligne ne la débitera pas.
        if (entry.tx_id != null) {
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(TeddTokens.RFull))
                    .background(TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RFull))
                    .padding(horizontal = 8.dp, vertical = 2.dp),
            ) { Text("payé", color = TeddTokens.Ink, fontSize = 10.5.sp, fontWeight = FontWeight.SemiBold) }
        }
        Text(money(entry.amount, currency), color = TeddTokens.Ink, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
        Box(
            modifier = Modifier.size(28.dp).clip(RoundedCornerShape(TeddTokens.RXs)).clickable(onClick = onDelete),
            contentAlignment = Alignment.Center,
        ) { TeddIcon(name = "trash", size = 13.dp, color = TeddTokens.InkMuted, strokeWidth = 1.6.dp) }
    }
}

@Composable
private fun WalletField(value: String, placeholder: String, digits: Boolean = false, mono: Boolean = false, onChange: (String) -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(44.dp)
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(horizontal = 12.dp),
        contentAlignment = Alignment.CenterStart,
    ) {
        val style = TextStyle(
            color = TeddTokens.Ink,
            fontSize = 14.sp,
            fontFamily = if (mono) LocalTeddFonts.current.mono else null,
        )
        if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkSubtle, style = style.copy(color = TeddTokens.InkSubtle))
        BasicTextField(
            value = value,
            onValueChange = onChange,
            singleLine = true,
            textStyle = style,
            cursorBrush = SolidColor(TeddTokens.LimeStrong),
            // Toute la largeur : un tap n'importe où dans la case place le curseur.
            modifier = Modifier.fillMaxWidth(),
            keyboardOptions = if (digits) KeyboardOptions(keyboardType = KeyboardType.Number) else KeyboardOptions.Default,
        )
    }
}
